import { authFetch } from "@/lib/api-client";
import { API_REQUEST_TIMEOUT_LONG_MS } from "@/lib/query-constants";
import { buildQueryTuningFromForm } from "../lib/persona-opportunity-form";
import type {
  CreatePersonaSetFormValues,
  PersonaOpportunityQueryTuning,
} from "../types/persona-opportunities.types";

// ─── Types ──────────────────────────────────────────────────────────

export type PersonaOpportunitySetStatus = "GENERATING" | "READY" | "FAILED";

/** Opportunity row occupancy — matches `PersonaOpportunityStatus` in `@nexus/db`. */
export type PersonaOpportunityStatus =
  | "NEW"
  | "SCRIPTS_GENERATED"
  | "BRIEFS_GENERATED"
  | "SCRIPTS_AND_BRIEFS_GENERATED"
  | "SENT_TO_VENDOR";

/** `persona_opportunity_sets.config` snapshot taken when the set was created. */
export interface PersonaOpportunitySetConfig {
  industry?: string | null;
  industryCompetitors?: string[] | null;
  adPlatforms?: string[] | null;
  candidatePoolSize?: number | null;
  anglesReturned?: number | null;
  maxAnglesPerFeature?: number | null;
  minBrandsPerFeature?: number | null;
  stickDays?: number | null;
}

/** Set row as serialized by the BFF — Prisma dates arrive as ISO strings. */
export interface PersonaOpportunitySetRow {
  id: string;
  tenantId: string;
  name: string;
  status: PersonaOpportunitySetStatus;
  customerPartition: string | null;
  startDate: string | null;
  endDate: string | null;
  config: PersonaOpportunitySetConfig | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Present on list responses (`_count.opportunities`). Detail responses omit it —
   * sum the persona opportunity arrays instead.
   */
  opportunityCount?: number;
  /** Non-deleted persona scripts on the set — list + detail. */
  scriptCount?: number;
}

export interface PersonaOpportunitySetListPage {
  items: PersonaOpportunitySetRow[];
  nextCursor: string | null;
  hasMore: boolean;
}

/** PRD §10 KPI snapshot denormalized onto each opportunity at generation time. */
export interface PersonaOpportunityKpis {
  drivingFeatureScore: number;
  creativesCount: number;
  competitorsRunningCount: number;
  stickRatePct: number;
  duplicatedPct: number;
  myInvestmentSharePct: number;
  marketInvestmentSharePct: number;
  investmentIndex?: number | null;
}

export interface PersonaOpportunitySupportingAd {
  id: string;
  opportunityId: string;
  hash: string | null;
  competitor: string | null;
  url: string | null;
  thumbnailUrl: string | null;
  /** Competitor brand logo from BQ `brands_metadata.profile_picture_url`. */
  profilePictureUrl?: string | null;
  daysLive: number | null;
  duplications: number | null;
  rank: number;
  createdAt: string;
}

export interface PersonaOpportunityRow {
  id: string;
  setId: string;
  personaId: string;
  status: PersonaOpportunityStatus;
  angle: string;
  painPoint: string | null;
  motivation: string | null;
  /** BQ `driven_by` — null on rows generated before the column existed. */
  drivenBy: "painpoint" | "motivation" | null;
  rank: number;
  /** Synthetic "Your Scripts" angle — hidden from the Opportunities tab. */
  isCustom?: boolean;
  kpis: PersonaOpportunityKpis;
  createdAt: string;
  updatedAt: string;
  supportingAd: PersonaOpportunitySupportingAd | null;
  provenanceCaption: string | null;
}

export interface PersonaOpportunitySetPersonaSection {
  personaId: string;
  title: string;
  who: string | null;
  pains: string[];
  motivations: string[];
  /** ISO timestamp once BigQuery stopped returning this persona; the set still shows it. */
  staledAt: string | null;
  opportunities: PersonaOpportunityRow[];
}

export interface PersonaOpportunitySetDetail {
  /** `competitorsTotal` / `scriptCount` are derived at read time. */
  set: PersonaOpportunitySetRow & { competitorsTotal: number; scriptCount?: number };
  personas: PersonaOpportunitySetPersonaSection[];
}

// ─── Read Endpoints ─────────────────────────────────────────────────

export interface FetchPersonaOpportunitySetsParams {
  cursor?: string;
  limit?: number;
}

export async function fetchPersonaOpportunitySets(
  params: FetchPersonaOpportunitySetsParams = {},
): Promise<PersonaOpportunitySetListPage> {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.limit != null) qs.set("limit", String(params.limit));
  const query = qs.size > 0 ? `?${qs}` : "";

  const { data } = await authFetch<PersonaOpportunitySetListPage>(
    `/persona-opportunity-sets${query}`,
    { timeoutMs: API_REQUEST_TIMEOUT_LONG_MS },
  );
  return data;
}

export async function fetchPersonaOpportunitySetDetail(
  id: string,
): Promise<PersonaOpportunitySetDetail> {
  const { data } = await authFetch<PersonaOpportunitySetDetail>(
    `/persona-opportunity-sets/${encodeURIComponent(id)}`,
    // Detail fans out to personas, opportunities, and supporting ads in one call.
    { timeoutMs: API_REQUEST_TIMEOUT_LONG_MS },
  );
  return data;
}

// ─── Generate (Amber Path) ──────────────────────────────────────────

export interface GeneratePersonaOpportunitySetRequest {
  tenantId: string;
  idempotencyKey: string;
  name: string;
  personaIds: string[];
  industry: string;
  industryCompetitors: string[];
  startDate: string;
  endDate: string;
  /** v1 accepts a single platform — BQ filters on one `@platform`. */
  adPlatforms?: string[];
  advanced?: PersonaOpportunityQueryTuning;
}

function hasTuningOverrides(form: CreatePersonaSetFormValues): boolean {
  return form.showAdvanced;
}

/**
 * Maps create-dialog form state to the generate request body.
 *
 * The date pair is always sent. `advanced` is omitted unless the caller holds
 * `PERSONA_OPPORTUNITY_SETS_QUERY_TUNING` — the BFF 403s when it arrives without that grant.
 */
export function buildGeneratePersonaOpportunitySetRequest(
  form: CreatePersonaSetFormValues,
  context: { tenantId: string; idempotencyKey: string; canUseQueryTuning: boolean },
): GeneratePersonaOpportunitySetRequest {
  const adPlatform = form.adPlatform.trim();
  const includeAdvanced = context.canUseQueryTuning && hasTuningOverrides(form);

  return {
    tenantId: context.tenantId,
    idempotencyKey: context.idempotencyKey,
    name: form.name.trim(),
    personaIds: form.personaIds,
    industry: form.industry,
    industryCompetitors: form.industryCompetitors,
    startDate: form.startDate,
    endDate: form.endDate,
    ...(adPlatform ? { adPlatforms: [adPlatform] } : {}),
    ...(includeAdvanced ? { advanced: buildQueryTuningFromForm(form) } : {}),
  };
}

/** Request-tenant own brand slug for competitor exclusion in the create dialog. */
export interface PersonaOwnBrand {
  slug: string | null;
}

export async function fetchPersonaOwnBrand(): Promise<PersonaOwnBrand> {
  const { data } = await authFetch<PersonaOwnBrand>("/persona-opportunities/own-brand");
  return data;
}

/** POSTs the generate request and returns the Amber correlation id from the 202. */
export async function generatePersonaOpportunitySet(
  body: GeneratePersonaOpportunitySetRequest,
): Promise<{ correlationId: string }> {
  const { correlationId } = await authFetch<void>("/persona-opportunity-sets/generate", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!correlationId) {
    throw new Error("Generate response missing correlationId");
  }
  return { correlationId };
}

// ─── Delete / retry / regenerate ────────────────────────────────────

export async function deletePersonaOpportunitySet(id: string): Promise<void> {
  await authFetch(`/persona-opportunity-sets/${encodeURIComponent(id)}`, {
    method: "DELETE",
    body: JSON.stringify({}),
    rawResponse: true,
  });
}

export interface RetryPersonaOpportunitySetRequest {
  tenantId: string;
  idempotencyKey: string;
}

export async function retryPersonaOpportunitySet(
  id: string,
  body: RetryPersonaOpportunitySetRequest,
): Promise<{ correlationId: string }> {
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(id)}/retry`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  if (!correlationId) {
    throw new Error("Retry response missing correlationId");
  }
  return { correlationId };
}

export type RegeneratePersonaOpportunitySetMode = "overwrite" | "new_set";

export interface RegeneratePersonaOpportunitySetRequest {
  tenantId: string;
  idempotencyKey: string;
  mode: RegeneratePersonaOpportunitySetMode;
  /** Required when `mode === "new_set"`. */
  name?: string;
}

export async function regeneratePersonaOpportunitySet(
  id: string,
  body: RegeneratePersonaOpportunitySetRequest,
): Promise<{ correlationId: string }> {
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(id)}/regenerate`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  if (!correlationId) {
    throw new Error("Regenerate response missing correlationId");
  }
  return { correlationId };
}
